import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, Button, Typography } from './ui';

const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function OnboardingSuccess() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionData, setSessionData] = useState(null);

  useEffect(() => {
    const verifySession = async () => {
      const sessionId = searchParams.get('session_id');
      
      if (!sessionId) {
        setError('No session ID found');
        setLoading(false);
        return;
      }

      try {
        // Verify the session was successful and get onboarding data
        const response = await fetch(`${baseURL}/api/payment/verify-session?session_id=${sessionId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setSessionData(data);
          console.log('✅ Session verified with data:', data);
          setLoading(false);
        } else {
          throw new Error('Session verification failed');
        }
      } catch (err) {
        console.error('Error verifying session:', err);
        setError('Payment verification failed. Please contact support.');
        setLoading(false);
      }
    };

    verifySession();
  }, [searchParams, refreshUser]);

  const handleContinueToDashboard = async () => {
    try {
      // Prepare update data with journey stage and onboarding data
      const updateData = {
        journeyStage: 'trial',
        subscriptionStatus: 'trialing'
      };

      // Add onboarding data if available from session
      if (sessionData?.userData) {
        const userData = sessionData.userData;
        
        // Add billing plan based on session data
        if (sessionData.planType) {
          updateData.billingPlan = sessionData.planType === 'monthly' ? 'Monthly' : 'Quarterly';
        }
        
        // Add onboarding fields
        if (userData.firstName) updateData.firstName = userData.firstName;
        if (userData.lastName) updateData.lastName = userData.lastName;
        if (userData.employmentStatus) updateData.employmentStatus = userData.employmentStatus;
        if (userData.jobSearchStatus) updateData.jobSearchStatus = userData.jobSearchStatus;
        if (userData.interestedRoleKeywords) updateData.interestedRoleKeywords = userData.interestedRoleKeywords;
        if (userData.currentSkills) updateData.currentSkills = userData.currentSkills;
        if (userData.softwareFamiliarity) updateData.softwareFamiliarity = userData.softwareFamiliarity;
        if (userData.linkedinUrl) updateData.linkedinUrl = userData.linkedinUrl;
        if (userData.githubUrl) updateData.githubUrl = userData.githubUrl;
        if (userData.personalWebsiteUrl) updateData.personalWebsiteUrl = userData.personalWebsiteUrl;
        
        console.log('💾 Saving onboarding data:', Object.keys(userData));
      }

      // Update user with journey stage and onboarding data
      const response = await fetch(`${baseURL}/api/users/me`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        console.log('✅ Journey stage and onboarding data saved successfully');

        // Check if there's a resume file to upload from localStorage
        const resumeFileData = localStorage.getItem('onboarding_resume_file');
        if (resumeFileData) {
          try {
            const { fileName, fileData, fileType } = JSON.parse(resumeFileData);
            console.log('📄 Found resume file to upload:', fileName);

            // Convert base64 back to File object
            const byteCharacters = atob(fileData);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const file = new File([byteArray], fileName, { type: fileType });

            // Upload the resume file
            const formData = new FormData();
            formData.append('cv', file);

            const uploadResponse = await fetch(`${baseURL}/api/cv/upload`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              },
              body: formData
            });

            if (uploadResponse.ok) {
              console.log('📄 Resume uploaded successfully');
              // Clean up localStorage
              localStorage.removeItem('onboarding_resume_file');
            } else {
              console.warn('⚠️ Failed to upload resume, but continuing');
            }
          } catch (fileError) {
            console.error('Error uploading resume:', fileError);
          }
        }

        // Refresh user data and navigate
        await refreshUser();
        navigate('/dashboard');
      } else {
        // If update fails, still navigate (better than being stuck)
        console.warn('⚠️ Failed to update user data, navigating anyway');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error updating user data:', error);
      // Still navigate even if there's an error
      navigate('/dashboard');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <div className="bg-white bg-opacity-5 rounded-lg border border-gray-200 border-opacity-20 p-8 text-center max-w-md mx-auto">
          <div className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-6"></div>
          <h2 className="text-2xl font-bold text-white mb-4">Setting up your account...</h2>
          <p className="text-gray-400">We're processing your subscription and setting up your Elena experience.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <div className="bg-white bg-opacity-5 rounded-lg border border-red-500 border-opacity-50 p-8 text-center max-w-md mx-auto">
          <svg className="w-16 h-16 text-red-400 mx-auto mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h2 className="text-2xl font-bold text-white mb-4">Something went wrong</h2>
          <p className="text-gray-400 mb-6">{error}</p>
          <button
            onClick={() => navigate('/onboarding')}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200"
          >
            Return to Onboarding
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      <div className="bg-white bg-opacity-5 rounded-lg border border-gray-200 border-opacity-20 p-8 md:p-12 text-center max-w-2xl mx-auto">
        {/* Success Icon */}
        <div className="mb-8">
          <div className="w-20 h-20 bg-green-500 bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-green-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          </div>
        </div>

        {/* Welcome Message */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-4">
            Welcome to <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">Elena</span>!
          </h1>
          <p className="text-xl text-gray-300 mb-6">
            Your 7-day free trial has started successfully
          </p>
          <p className="text-gray-400">
            We've set up your account and you're ready to start optimizing your job search with AI-powered insights.
          </p>
        </div>

        {/* What's Next */}
        <div className="bg-blue-500 bg-opacity-10 rounded-lg p-6 mb-8 text-left">
          <h3 className="text-lg font-semibold text-white mb-4">What's next?</h3>
          <div className="space-y-3">
            <div className="flex items-center text-gray-300">
              <svg className="w-5 h-5 text-green-400 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Upload your first resume for AI analysis
            </div>
            <div className="flex items-center text-gray-300">
              <svg className="w-5 h-5 text-green-400 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Start tracking your job applications
            </div>
            <div className="flex items-center text-gray-300">
              <svg className="w-5 h-5 text-green-400 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Get personalized job insights and recommendations
            </div>
          </div>
        </div>

        {/* Free Trial Info */}
        <div className="bg-yellow-500 bg-opacity-10 border border-yellow-500 border-opacity-30 rounded-lg p-4 mb-8">
          <div className="flex items-center justify-center text-yellow-300">
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className="text-sm font-medium">
              Your trial ends in 7 days. Cancel anytime from Settings.
            </span>
          </div>
        </div>

        {/* Continue Button */}
        <button
          onClick={handleContinueToDashboard}
          className="w-full md:w-auto px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white font-semibold text-lg rounded-lg hover:from-blue-600 hover:to-purple-600 transform hover:scale-105 transition-all duration-200 shadow-lg"
        >
          Continue to Dashboard
        </button>
      </div>
    </div>
  );
}

export default OnboardingSuccess; 